import { ApiError } from "@allergy123/api-client";
import { useApi } from "@allergy123/api-client/react";
import { useAuth } from "@allergy123/auth-client/react";
import { Button, Card, Field, Status } from "@allergy123/ui";
import { useState } from "react";

/**
 * Two-step verification for staff. The API refuses every admin route until the
 * signed-in person has an authenticator app enrolled, and Cognito's managed
 * login does not offer enrolment while the pool's MFA is optional — so the
 * console does it: associate a secret, verify one code, make it the preferred
 * factor. The three calls act on the caller only and are authorised by the
 * access token, which carries the `aws.cognito.signin.user.admin` scope for
 * this app client alone.
 */
const authority = (import.meta.env.VITE_AUTH_AUTHORITY as string | undefined) ?? "";
const region = (import.meta.env.VITE_AUTH_REGION as string | undefined) ?? authority.match(/\.auth\.([a-z0-9-]+)\.amazoncognito\.com/)?.[1] ?? "us-east-1";

async function cognito<T>(target: string, body: Record<string, unknown>): Promise<T> {
  const res = await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
    method: "POST",
    headers: { "content-type": "application/x-amz-json-1.1", "x-amz-target": `AWSCognitoIdentityProviderService.${target}` },
    body: JSON.stringify(body),
  });
  const json = (await res.json()) as T & { message?: string; __type?: string };
  if (!res.ok) throw new Error(json.message ?? json.__type ?? `Cognito returned ${res.status}`);
  return json;
}

export function SecurityPage() {
  const { session } = useAuth();
  const api = useApi();
  const [secret, setSecret] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [state, setState] = useState<{ kind: "idle" | "working" | "done" | "error"; text?: string }>({ kind: "idle" });
  const [test, setTest] = useState<{ kind: "idle" | "working" | "done" | "error"; text?: string }>({ kind: "idle" });
  const mock = !authority || session?.accessToken.startsWith("mock.");
  const account = session?.claims.email ?? "staff";
  const otpauth = secret ? `otpauth://totp/${encodeURIComponent(`Allergy 123 Admin:${account}`)}?secret=${secret}&issuer=${encodeURIComponent("Allergy 123 Admin")}` : "";

  /** The API picks the recipient (the signed-in account's own address) and audits the send; nothing is typed here. */
  async function sendTest() {
    setTest({ kind: "working" });
    try {
      const res = await api.admin.sendTestNotification();
      setTest({ kind: "done", text: `Test notification ${res.notificationId.slice(0, 8)} queued for ${account}. It arrives within a minute where this environment can send email; the local API only logs it.` });
    } catch (err) {
      const text = err instanceof ApiError && err.code === "mfa_required" ? "Turn on two-step verification first; the API refuses admin actions without it." : err instanceof ApiError && err.code === "notifications_unavailable" ? "This API has no notification producer configured." : `Couldn't queue the test notification (${err instanceof Error ? err.message : "error"}).`;
      setTest({ kind: "error", text });
    }
  }

  async function begin() {
    setState({ kind: "working" });
    try {
      const res = await cognito<{ SecretCode: string }>("AssociateSoftwareToken", { AccessToken: session!.accessToken });
      setSecret(res.SecretCode);
      setState({ kind: "idle" });
    } catch (e) {
      setState({ kind: "error", text: e instanceof Error ? e.message : "Couldn't start enrolment." });
    }
  }

  async function verify(e: React.FormEvent) {
    e.preventDefault();
    setState({ kind: "working" });
    try {
      const v = await cognito<{ Status: string }>("VerifySoftwareToken", { AccessToken: session!.accessToken, UserCode: code.trim(), FriendlyDeviceName: "Authenticator app" });
      if (v.Status !== "SUCCESS") throw new Error("That code wasn't accepted. Wait for the next one and try again.");
      await cognito("SetUserMFAPreference", { AccessToken: session!.accessToken, SoftwareTokenMfaSettings: { Enabled: true, PreferredMfa: true } });
      setSecret(null);
      setCode("");
      setState({ kind: "done", text: "Two-step verification is on. You'll be asked for a code at each sign-in, and the console's admin pages are available now." });
    } catch (err) {
      setState({ kind: "error", text: err instanceof Error ? err.message : "Couldn't verify the code." });
    }
  }

  return (
    <>
      <h1 className="mb-2 text-h1 font-semibold text-anchor">Security</h1>
      <p className="mb-8 max-w-[68ch] text-ink-secondary">Staff access requires two-step verification with an authenticator app. Until it is on, the console's other pages stay closed to this account.</p>
      <Card className="max-w-[640px]" as="section">
        <h2 className="mb-4 text-h3 font-semibold text-anchor">Two-step verification</h2>
        {mock ? (
          <Status kind="info" label="Synthetic sign-in: there is no Cognito account to enrol. This page works against a real pool." />
        ) : state.kind === "done" ? (
          <Status kind="success" label={state.text!} />
        ) : !secret ? (
          <>
            <p className="mb-6">You'll add Allergy 123 Admin to an authenticator app (Microsoft Authenticator, 1Password, Google Authenticator), then confirm with one code.</p>
            <Button onClick={() => void begin()} disabled={state.kind === "working"}>
              {state.kind === "working" ? "Starting…" : "Set up an authenticator app"}
            </Button>
          </>
        ) : (
          <form onSubmit={(e) => void verify(e)} className="grid gap-5">
            <ol className="grid list-decimal gap-3 pl-5">
              <li>
                In your authenticator app, add an account and enter this key by hand, or open the link on a device that has the app.
                <div className="mt-2 break-all rounded-sm border border-rule-strong bg-card-tinted px-3 py-2 text-small tabular-nums">{secret}</div>
                <a className="mt-2 inline-block text-small" href={otpauth}>
                  Open in an authenticator app
                </a>
              </li>
              <li>Enter the six-digit code the app shows.</li>
            </ol>
            <Field label="Code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} required />
            <div>
              <Button type="submit" disabled={state.kind === "working" || code.trim().length !== 6}>
                {state.kind === "working" ? "Verifying…" : "Turn on two-step verification"}
              </Button>
            </div>
          </form>
        )}
        {state.kind === "error" && (
          <div className="mt-4">
            <Status kind="error" label={state.text ?? "Something went wrong."} />
          </div>
        )}
      </Card>
      <Card className="mt-6 max-w-[640px]" as="section">
        <h2 className="mb-4 text-h3 font-semibold text-anchor">Email delivery</h2>
        <p className="mb-6">Send yourself a generic test message to confirm this environment delivers email. It goes to the address on your account only, and the send is recorded in Audit as <code>admin.notification.test</code>.</p>
        <Button variant="secondary" surface="card" onClick={() => void sendTest()} disabled={test.kind === "working"}>
          {test.kind === "working" ? "Sending…" : "Send me a test email"}
        </Button>
        {test.kind === "done" && (
          <div className="mt-4">
            <Status kind="success" label={test.text!} />
          </div>
        )}
        {test.kind === "error" && (
          <div className="mt-4">
            <Status kind="error" label={test.text ?? "Something went wrong."} />
          </div>
        )}
      </Card>
    </>
  );
}
